import { ReactNode, useEffect, useState } from 'react';
import { Control, Controller, ControllerProps as BaseControllerProps, FieldError, useWatch } from 'react-hook-form';

import { Autocomplete, Rate, Space } from '@sravni/react-design-system';
import { RateProps } from '@sravni/react-design-system/dist/types/components/Rate';
import type { AutocompleteProps } from '@sravni/react-design-system/lib/Autocomplete';
import type { CheckboxProps as BaseCheckboxProps } from '@sravni/react-design-system/lib/Checkbox';
import { Checkbox } from '@sravni/react-design-system/lib/Checkbox';
import type { DateInputProps } from '@sravni/react-design-system/lib/DateInput';
import { DateInput } from '@sravni/react-design-system/lib/DateInput';
import { FormControl as BaseFormControl } from '@sravni/react-design-system/lib/FormControl';
import type { MaskInputProps } from '@sravni/react-design-system/lib/MaskInput';
import { MaskInput } from '@sravni/react-design-system/lib/MaskInput';
import type { NumberInputProps } from '@sravni/react-design-system/lib/NumberInput';
import { NumberInput } from '@sravni/react-design-system/lib/NumberInput';
import type { RadioButtonProps } from '@sravni/react-design-system/lib/RadioButton';
import { RadioButton } from '@sravni/react-design-system/lib/RadioButton';
import type { SelectProps } from '@sravni/react-design-system/lib/Select';
import { Select } from '@sravni/react-design-system/lib/Select';
import type { TextareaProps } from '@sravni/react-design-system/lib/Textarea';
import { Textarea } from '@sravni/react-design-system/lib/Textarea';
import type { TextInputProps } from '@sravni/react-design-system/lib/TextInput';
import { TextInput } from '@sravni/react-design-system/lib/TextInput';
import { useStyles } from '@sravni/react-utils';

import Yup, { sanitizeAndTypograf } from '../../../utils/yup';
import { LENGTH_MAX, TEXT_FIELDS } from '../../CreateReview/constants/common';

import { CONTROL_TYPES_WITH_COUNTER } from './constants';
import styles from './form-control.module.scss';
import { TUserChangeHandler } from './types';

type FieldValues = Record<string, any>;
type ControllerProps<F extends FieldValues> = Omit<BaseControllerProps<F>, 'render'> & {
  control: Control<F>;
};
type CheckboxProps = BaseCheckboxProps & {
  label: ReactNode;
};

type InputTypeMapping = {
  text: TextInputProps;
  number: NumberInputProps;
  mask: MaskInputProps;
  select: SelectProps;
  checkbox: CheckboxProps;
  'radio-button': RadioButtonProps;
  date: DateInputProps;
  textarea: TextareaProps;
  autocomplete: AutocompleteProps;
  rates: RateProps;
};

type ControlType = keyof InputTypeMapping;

type FormInputProps<T extends ControlType> = InputTypeMapping[T];

type FormControlProps<T extends ControlType, F extends FieldValues> = FormInputProps<T> &
  ControllerProps<F> & {
    controlType: T;
    error?: Partial<FieldError>;
    isShowCounter?: boolean;
    fieldClassName?: string;
    helperText?: string;
    handleUserChange?: TUserChangeHandler;
  };

export const FormControl = <T extends ControlType, F extends FieldValues>(props: FormControlProps<T, F>) => {
  const {
    className,
    controlType,
    name,
    control,
    rules,
    error,
    fieldClassName,
    helperText,
    isShowCounter = false,
    handleUserChange,
    ...propsRest
  } = props;

  const cx = useStyles(styles);
  const classNameProp = { className: fieldClassName };
  const [fieldLength, setFieldLength] = useState(null);

  const value = useWatch({ name, control });

  useEffect(() => {
    if (!isShowCounter && !CONTROL_TYPES_WITH_COUNTER.includes(controlType)) return;

    setFieldLength(value?.length);
  }, [controlType, isShowCounter, value]);

  const isCharCountVisible = isShowCounter && Boolean(fieldLength) && fieldLength > 0;

  return (
    <BaseFormControl className={className} invalid={Boolean(error)}>
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field }) => {
          const onChange = (value) => {
            field.onChange?.(value);

            handleUserChange?.({ name: field.name, value: value });
          };

          const onBlur = (element) => {
            // @ts-ignore
            if (TEXT_FIELDS.includes(field.name)) {
              const newValue = Yup.string().transform(sanitizeAndTypograf).validateSync(element.target?.value);

              field.onChange?.(newValue);
            }
          };

          const commonProps = {
            ...field,
            ...classNameProp,
            onChange,
            onBlur,
          };

          switch (controlType) {
            case 'text':
              return <TextInput {...commonProps} {...(propsRest as TextInputProps)} />;
            case 'number':
              return <NumberInput {...commonProps} {...(propsRest as NumberInputProps)} />;
            case 'mask':
              return <MaskInput {...commonProps} {...(propsRest as MaskInputProps)} />;
            case 'select':
              return <Select {...commonProps} {...((propsRest as unknown) as SelectProps)} />;
            case 'autocomplete':
              const { options } = (props as unknown) as AutocompleteProps;

              const handleChange = (label: string) => {
                const value = options.find((option) => option.label === label)?.value;

                field.onChange?.(value ?? null);
                handleUserChange?.({ name: field.name, value: label });
              };

              return (
                <Autocomplete
                  {...field}
                  {...classNameProp}
                  onChange={handleChange}
                  {...((propsRest as unknown) as AutocompleteProps)}
                />
              );
            case 'radio-button':
              return <RadioButton {...commonProps} {...((propsRest as unknown) as RadioButtonProps)} />;
            case 'date':
              return <DateInput {...commonProps} {...(propsRest as DateInputProps)} />;
            case 'textarea':
              return <Textarea {...commonProps} {...(propsRest as TextareaProps)} />;
            case 'checkbox': {
              const { value, ...fieldRest } = field;
              const { label, ..._propsRest } = (propsRest as unknown) as CheckboxProps;
              return (
                <Checkbox
                  checked={Boolean(value)}
                  {...fieldRest}
                  onChange={onChange}
                  {...classNameProp}
                  {..._propsRest}
                >
                  {label}
                </Checkbox>
              );
            }
            case 'rates': {
              return (
                <div
                  ref={field.ref}
                  tabIndex={-1} // делает элемент программно фокусируемым
                  style={{ display: 'inline-block' }}
                >
                  <Rate {...commonProps} {...((propsRest as unknown) as RateProps)} />
                </div>
              );
            }
            default:
              throw new Error('Invalid controller type');
          }
        }}
      />
      <Space direction={'horizontal'}>
        <BaseFormControl.HelperText>{error ? error?.message : helperText}</BaseFormControl.HelperText>
        {isCharCountVisible && (
          <BaseFormControl.HelperText className={cx('h-text-right', 'charCounter')}>
            {fieldLength}/{LENGTH_MAX[name.toString()]}
          </BaseFormControl.HelperText>
        )}
      </Space>
    </BaseFormControl>
  );
};
